// ============================================================
// ByOurConsent.com -- Order Confirmation Page
// ============================================================

import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Nav    from '../components/Nav';
import Footer from '../components/Footer';
import api    from '../utils/api';

const OrderConfirmPage = () => {
    const { orderNumber }  = useParams();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!orderNumber) return;
        api.get(`/orders/${orderNumber}`)
            .then(res => setOrder(res.data.order))
            .catch(() => setOrder(null))
            .finally(() => setLoading(false));
    }, [orderNumber]);

    return (
        <>
            <Helmet>
                <title>Order Confirmed — By Our Consent</title>
            </Helmet>

            <Nav />

            <div style={{ background: '#f5f0e8', minHeight: 'calc(100vh - 56px)' }}>

                {/* Green confirmation banner */}
                <div style={{
                    background:   '#1e2a3a',
                    padding:      '2rem 1.5rem',
                    textAlign:    'center',
                    borderBottom: '2px solid #3b6d11'
                }}>
                    <div style={{ fontSize: '40px', marginBottom: '10px' }}>✓</div>
                    <h1 style={{
                        fontSize:     '28px',
                        fontWeight:   600,
                        color:        '#e8dfc8',
                        fontFamily:   "'EB Garamond', Georgia, serif",
                        marginBottom: '6px'
                    }}>
                        Order confirmed!
                    </h1>
                    <div style={{
                        fontSize:   '14px',
                        fontFamily: 'sans-serif',
                        color:      '#6a7a8a'
                    }}>
                        Order #{orderNumber}
                    </div>
                </div>

                <div style={{ maxWidth: '640px', margin: '0 auto', padding: '2rem 1.5rem' }}>

                    {loading ? (
                        <div style={{
                            textAlign:  'center',
                            padding:    '2rem',
                            fontFamily: 'sans-serif',
                            color:      '#8a8070'
                        }}>
                            Loading order details...
                        </div>
                    ) : (
                        <>
                            {/* What happens next */}
                            <div style={{
                                background:   '#ede8dc',
                                border:       '0.5px solid #c8beaa',
                                borderRadius: '4px',
                                padding:      '1.25rem',
                                marginBottom: '1.5rem'
                            }}>
                                <div style={{
                                    fontSize:      '14px',
                                    fontWeight:    600,
                                    color:         '#1e2a3a',
                                    marginBottom:  '14px',
                                    paddingBottom: '10px',
                                    borderBottom:  '0.5px solid #c8beaa',
                                    fontFamily:    "'EB Garamond', Georgia, serif"
                                }}>
                                    What happens next
                                </div>
                                {[
                                    {
                                        icon:  '📧',
                                        title: 'Confirmation email',
                                        desc:  'Check your inbox for your order confirmation.'
                                    },
                                    {
                                        icon:  '🖨️',
                                        title: 'Printing & fulfillment',
                                        desc:  'Your order goes to Printful for printing. This takes 2–5 business days.'
                                    },
                                    {
                                        icon:  '📦',
                                        title: 'Shipping',
                                        desc:  "We'll email you a tracking number when your order ships. Delivery in 3–7 business days after printing."
                                    }
                                ].map(({ icon, title, desc }) => (
                                    <div key={title} style={{
                                        display:      'flex',
                                        gap:          '12px',
                                        marginBottom: '12px',
                                        alignItems:   'flex-start'
                                    }}>
                                        <span style={{ fontSize: '20px', flexShrink: 0 }}>{icon}</span>
                                        <div>
                                            <div style={{
                                                fontSize:     '13px',
                                                fontWeight:   600,
                                                color:        '#1e2a3a',
                                                marginBottom: '2px',
                                                fontFamily:   'sans-serif'
                                            }}>
                                                {title}
                                            </div>
                                            <div style={{
                                                fontSize:   '12px',
                                                fontFamily: 'sans-serif',
                                                color:      '#5a6070',
                                                lineHeight: 1.5
                                            }}>
                                                {desc}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Order details */}
                            {order && (
                                <div style={{
                                    background:   '#f5f0e8',
                                    border:       '0.5px solid #c8beaa',
                                    borderRadius: '4px',
                                    padding:      '1.25rem',
                                    marginBottom: '1.5rem'
                                }}>
                                    <div style={{
                                        fontSize:      '14px',
                                        fontWeight:    600,
                                        color:         '#1e2a3a',
                                        marginBottom:  '14px',
                                        paddingBottom: '10px',
                                        borderBottom:  '0.5px solid #c8beaa',
                                        fontFamily:    "'EB Garamond', Georgia, serif"
                                    }}>
                                        Order details
                                    </div>

                                    {/* Items */}
                                    {order.items?.map((item, i) => (
                                        <div key={i} style={{
                                            display:        'flex',
                                            justifyContent: 'space-between',
                                            marginBottom:   '8px',
                                            fontSize:       '13px',
                                            fontFamily:     'sans-serif'
                                        }}>
                                            <div>
                                                <span style={{ color: '#1e2a3a', fontWeight: 600 }}>
                                                    {item.product_name}
                                                </span>
                                                {item.variant_desc && (
                                                    <span style={{ color: '#8a8070', marginLeft: '6px' }}>
                                                        ({item.variant_desc})
                                                    </span>
                                                )}
                                                {item.quantity > 1 && (
                                                    <span style={{ color: '#8a8070' }}>
                                                        {' '}× {item.quantity}
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{ color: '#1e2a3a' }}>
                                                ${(item.total_price_cents / 100).toFixed(2)}
                                            </div>
                                        </div>
                                    ))}

                                    {/* Totals */}
                                    <div style={{
                                        borderTop:  '0.5px solid #c8beaa',
                                        paddingTop: '10px',
                                        marginTop:  '8px'
                                    }}>
                                        {[
                                            {
                                                label: 'Subtotal',
                                                value: `$${(order.subtotal_cents / 100).toFixed(2)}`
                                            },
                                            {
                                                label: 'Shipping',
                                                value: order.shipping_cents === 0
                                                    ? 'FREE'
                                                    : `$${(order.shipping_cents / 100).toFixed(2)}`
                                            },
                                            {
                                                label: 'Total',
                                                value: `$${(order.total_cents / 100).toFixed(2)}`,
                                                bold:  true
                                            }
                                        ].map(({ label, value, bold }) => (
                                            <div key={label} style={{
                                                display:        'flex',
                                                justifyContent: 'space-between',
                                                marginBottom:   '6px',
                                                fontSize:       bold ? '15px' : '13px',
                                                fontFamily:     bold
                                                    ? "'EB Garamond', Georgia, serif"
                                                    : 'sans-serif',
                                                fontWeight:     bold ? 600 : 400,
                                                color:          '#1e2a3a'
                                            }}>
                                                <span>{label}</span>
                                                <span>{value}</span>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Shipping address */}
                                    {order.ship_address1 && (
                                        <div style={{
                                            borderTop:  '0.5px solid #c8beaa',
                                            paddingTop: '10px',
                                            marginTop:  '8px',
                                            fontSize:   '12px',
                                            fontFamily: 'sans-serif',
                                            color:      '#6a7080'
                                        }}>
                                            <div style={{ fontWeight: 600, marginBottom: '2px' }}>
                                                Shipping to:
                                            </div>
                                            <div>{order.ship_name}</div>
                                            <div>{order.ship_address1}</div>
                                            {order.ship_address2 && <div>{order.ship_address2}</div>}
                                            <div>
                                                {order.ship_city}, {order.ship_state_province} {order.ship_postal_code}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* CTAs */}
                            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                <Link to="/account?tab=orders" style={{ background: '#8b1a1a', color: '#f5f0e8', padding: '10px 20px', fontSize: '12px', fontFamily: 'sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase', textDecoration:'none', borderRadius: '3px' }}>
                                    View my orders
                                </Link>
                                <Link to="/store" style={{ background: 'transparent', color: '#4a5060', padding: '10px 20px', fontSize: '12px', fontFamily: 'sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase', textDecoration:'none', borderRadius: '3px', border: '0.5px solid #c8beaa' }}>
                                    Continue shopping
                                </Link>
                                <Link to="/" style={{ background: 'transparent', color: '#4a5060', padding: '10px 20px', fontSize: '12px', fontFamily: 'sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase', textDecoration:'none', borderRadius: '3px', border: '0.5px solid #c8beaa' }}>
                                    Return home
                                </Link>
                            </div>

                            {/* Share the movement */}
                            <div style={{
                                marginTop:    '2rem',
                                padding:      '1.25rem',
                                background:   '#1e2a3a',
                                borderRadius: '4px',
                                textAlign:    'center'
                            }}>
                                <div style={{
                                    fontSize:     '16px',
                                    fontWeight:   600,
                                    color:        '#e8dfc8',
                                    fontFamily:   "'EB Garamond', Georgia, serif",
                                    marginBottom: '6px'
                                }}>
                                    Share the movement
                                </div>
                                <div style={{
                                    fontSize:     '12px',
                                    fontFamily:   'sans-serif',
                                    color:        '#6a7a8a',
                                    marginBottom: '14px'
                                }}>
                                    Tell your friends about ByOurConsent.com
                                </div>
                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                    {[
                                        {
                                            label: 'Share on X',
                                            url:   `https://twitter.com/intent/tweet?text=I just joined the movement at ByOurConsent.com — constitutional governance, civic accountability, no party affiliation.&url=https://byourconsent.com`
                                        },
                                        {
                                            label: 'Share on Facebook',
                                            url:   `https://www.facebook.com/sharer/sharer.php?u=https://byourconsent.com`
                                        }
                                    ].map(({ label, url }) => (
                                        <a
                                            key={label}
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                background:    'rgba(255,255,255,0.1)',
                                                color:         '#e8dfc8',
                                                padding:       '8px 16px',
                                                fontSize:      '11px',
                                                fontFamily:    'sans-serif',
                                                letterSpacing: '0.06em',
                                                textTransform: 'uppercase',
                                                textDecoration:'none',
                                                borderRadius:  '3px',
                                                border:        '0.5px solid rgba(255,255,255,0.15)'
                                            }}>
                                            {label}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <Footer />
        </>
    );
};

export default OrderConfirmPage;
